import * as turf from '@turf/turf'
import type { Feature, FeatureCollection } from 'geojson'

import type { OperationalLayerRow } from '../types'

import { gisFolderLabelFromLayer } from './folderFacetMatch'
import { foldSearchText } from './foldSearchText'
import { layerSharingKind } from './layerSharingKind'
import { normalizeLayerGeoJSON } from './normalizeLayerGeoJSON'

export type LayerSearchRow = {
  layerId: number
  layerName: string
  haystack: string
  sharingKind: 'public' | 'shared'
  category: string | null
  flyFeature: Feature
  featureCount: number
  propertiesOrder: string[] | null
  layerCreatorUserId?: number | null
  layerCreatorUsername?: string | null
}

function flyFeatureFromFc(fc: FeatureCollection): Feature | null {
  try {
    const coll = fc as turf.FeatureCollection
    const b = turf.bbox(coll)
    if (!b.every(n => Number.isFinite(n))) return null
    const [w, s, e, n] = b
    if (Math.abs(e - w) < 1e-12 && Math.abs(n - s) < 1e-12) {
      return turf.centroid(coll) as Feature
    }
    return turf.bboxPolygon(b) as Feature
  } catch (err) {
    console.warn('[smart-search] flyFeatureFromFc failed', err)
    return null
  }
}

function buildHaystackParts(
  layerName: string,
  layer: OperationalLayerRow,
  sharing: 'public' | 'shared',
  folderLabel: string | null,
): string {
  const legacyCat =
    typeof layer.category === 'string' && layer.category.trim()
      ? layer.category.trim()
      : null
  const desc =
    typeof layer.description === 'string' ? layer.description.trim() : ''

  const hayParts = [
    foldSearchText(layerName),
    foldSearchText(desc),
    folderLabel ? foldSearchText(folderLabel) : '',
    sharing === 'public'
      ? `${foldSearchText('publique')} public`
      : `${foldSearchText('partagée')} ${foldSearchText('partage')} ${foldSearchText('restreint')}`,
    legacyCat === 'lift'
      ? `${foldSearchText('remontée')} ${foldSearchText('télécabine')} ${foldSearchText('téléphérique')} lift`
      : '',
    legacyCat === 'infrastructure' ? foldSearchText('infrastructure') : '',
    legacyCat === 'general' ? 'general' : '',
  ]

  if (layer.user_id != null) {
    hayParts.push(foldSearchText(String(layer.user_id)))
  }
  if (layer.layer_creator_username) {
    hayParts.push(foldSearchText(layer.layer_creator_username))
  }

  return hayParts.filter(Boolean).join(' ')
}

function resolveCreator(layer: OperationalLayerRow): {
  layerCreatorUserId: number | null
  layerCreatorUsername: string | null
} {
  const raw =
    layer.user_id !== undefined && layer.user_id !== null
      ? Number(layer.user_id)
      : null
  return {
    layerCreatorUserId: raw !== null && !Number.isNaN(raw) ? raw : null,
    layerCreatorUsername: layer.layer_creator_username?.trim() || null,
  }
}

/**
 * Build search rows from metadata when available (bbox + feature_count),
 * falling back to legacy client-side GeoJSON (turf) when bbox is absent.
 */
export function buildLayerSearchRows(
  layers: OperationalLayerRow[],
): LayerSearchRow[] {
  const rows: LayerSearchRow[] = []

  for (const layer of layers) {
    const layerName = layer.name?.trim() || `Couche ${layer.id}`
    const sharing = layerSharingKind(layer)
    const folderLabel = gisFolderLabelFromLayer(layer)
    const haystack = buildHaystackParts(layerName, layer, sharing, folderLabel)
    const creator = resolveCreator(layer)
    const propertiesOrder = Array.isArray(layer.properties_order)
      ? layer.properties_order
      : null

    // --- Metadata path (post-migration: bbox + feature_count available) ---
    if (layer.bbox) {
      const featureCount = layer.feature_count ?? 0
      if (featureCount === 0) continue

      const flyFeature: Feature = {
        type: 'Feature',
        properties: {},
        geometry: layer.bbox,
      }

      rows.push({
        layerId: layer.id,
        layerName,
        haystack,
        sharingKind: sharing,
        category: folderLabel,
        flyFeature,
        featureCount,
        propertiesOrder,
        ...creator,
      })
      continue
    }

    // --- Legacy fallback: no bbox metadata, derive from layer.data ---
    const fc = normalizeLayerGeoJSON(layer)
    if (!fc?.features?.length) continue

    const flyFeature = flyFeatureFromFc(fc)
    if (!flyFeature?.geometry) continue

    rows.push({
      layerId: layer.id,
      layerName,
      haystack,
      sharingKind: sharing,
      category: folderLabel,
      flyFeature,
      featureCount: fc.features.length,
      propertiesOrder,
      ...creator,
    })
  }

  return rows
}
